/**
 * Componente compacto de seguimiento de objetivos
 * Muestra botoncitos con contadores que se actualizan en tiempo real
 */

import React from 'react';
import { LessonObjective, UserLessonProgress } from '../../services/academicService';
import './ObjectivesCompactTracker.css';

interface ObjectivesCompactTrackerProps {
  objectives: LessonObjective[];
  progress: UserLessonProgress | null;
  onInfoClick: () => void;
  className?: string;
}

// Tipos de objetivos según curriculum_data.json
type ObjectiveType = 'gramatica' | 'vocabulario' | 'comunicativo';

interface ObjectiveCategory {
  type: ObjectiveType;
  icon: string;
  label: string;
  completed: number;
  total: number;
}

export const ObjectivesCompactTracker: React.FC<ObjectivesCompactTrackerProps> = ({
  objectives,
  progress,
  onInfoClick,
  className = '',
}) => {
  // Categorizar objetivos por tipo
  const categorizeObjectives = (): ObjectiveCategory[] => {
    const categories: Record<ObjectiveType, ObjectiveCategory> = {
      gramatica: { type: 'gramatica', icon: '📝', label: 'Gramática', completed: 0, total: 0 },
      vocabulario: { type: 'vocabulario', icon: '📚', label: 'Vocabulario', completed: 0, total: 0 },
      comunicativo: { type: 'comunicativo', icon: '💬', label: 'Comunicativo', completed: 0, total: 0 },
    };

    objectives.forEach((objective) => {
      // Buscar el objetivo en curriculum_data para obtener su tipo
      // Por ahora, inferimos del description o usamos un campo type si existe
      const type = (objective as any).type as ObjectiveType | undefined;
      
      if (type && categories[type]) {
        categories[type].total++;
        
        // Verificar si está completado
        if (progress?.objectives_status?.[objective.description]?.completed) {
          categories[type].completed++;
        }
      }
    });

    // Retornar solo las categorías que tienen objetivos
    return Object.values(categories).filter((cat) => cat.total > 0);
  };

  const categories = categorizeObjectives();

  // Si no hay objetivos, no mostrar nada
  if (objectives.length === 0) {
    return null;
  }

  return (
    <div className={`objectives-compact-tracker ${className}`}>
      <div className="objectives-label">Objetivos:</div>
      <div className="objectives-buttons">
        {categories.map((category) => {
          const isAllCompleted = category.completed === category.total;
          const hasProgress = category.completed > 0;

          return (
            <button
              key={category.type}
              className={`objective-button ${
                isAllCompleted ? 'completed' : hasProgress ? 'in-progress' : 'pending'
              }`}
              onClick={onInfoClick}
              title={`${category.label}: ${category.completed}/${category.total}`}
              type="button"
            >
              <span className="objective-icon" aria-hidden="true">
                {category.icon}
              </span>
              <span className="objective-count">
                {category.completed}/{category.total}
              </span>
            </button>
          );
        })}
        <button
          className="objectives-info-button"
          onClick={onInfoClick}
          title="Ver detalles de objetivos"
          type="button"
          aria-label="Ver detalles de objetivos"
        >
          <span aria-hidden="true">ℹ️</span>
        </button>
      </div>
    </div>
  );
};
